import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useApiNoSqlContext } from 'src/modules/contexts/ApiNoSqlContext';
import {
	ApiError,
	Body_upload_single_file_api_v2_pipeline_upload_one_post,
	SfnSchema,
	UploadOneResponse,
	InvoicesErrorHistory,
	EmailBucketResponse,
	Body_do_retry_api_v2_pipeline_retry_post,
	Body_delete_invoice_upload_pipeline_error_api_v2_pipeline_resolve_post,
	Body_upload_pdf_to_email_bucket_api_v2_pipeline_upload_pdf_post
} from 'src/modules/generated-api-clients/no-sql';
import { AxiosError } from 'axios';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { logApiError, logAxiosError, logError, logRequest } from './helpers';
import { MUTATION_KEYS, QUERY_KEYS } from './enums';

/**
 * Custom hook to upload a single file to S3.
 * Endpoint: POST '/api/v2/pipeline/upload/one'
 * @returns An object containing the utility template list and error, if any.
 */
export const useMutationUploadSingleFile = () => {
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);
	const queryClient = useQueryClient();

	return useMutation<UploadOneResponse, ApiError, Body_upload_single_file_api_v2_pipeline_upload_one_post>(
		(body: Body_upload_single_file_api_v2_pipeline_upload_one_post) => {
			if (user.attributes === undefined) {
				throw new Error('AWS Amplify user attributes cannot be undefined');
			}
			if (!client) {
				throw new Error('API client is not initialized');
			}
			if (!body.file && !body.manager_id) {
				throw new Error('No file or manager_id provided');
			}
			const response = client.pipeline.uploadSingleFileApiV2PipelineUploadOnePost(body);
			logRequest(body, 'POST', MUTATION_KEYS.pipeline.UploadSingleFile, response);
			return response;
		},
		{
			onSuccess: (data: UploadOneResponse) => {
				console.log('Upload successful:', JSON.stringify(data));
				queryClient.invalidateQueries(QUERY_KEYS.invoices.AllInvoices);
				queryClient.invalidateQueries(QUERY_KEYS.utilities.AllUtilities);
				queryClient.invalidateQueries(QUERY_KEYS.prompts.AllPrompts);
				queryClient.invalidateQueries(QUERY_KEYS.common.DocumentCount);
				queryClient.invalidateQueries(QUERY_KEYS.ecsHealthCheck.SingleHealthCheck);
			},
			onError: (error: unknown) => {
				if (error instanceof ApiError) {
					logApiError(error);
				} else if (error instanceof AxiosError) {
					logAxiosError(error);
				} else {
					logError(error, 'Error uploading file');
				}
				return;
			}
		}
	);
};

/**
 * Custom hook to fetch a single Step-function execution status.
 * Endpoint: GET '/api/v2/read/single/sfn-execution'
 * @returns An object representing a Step-function Schema which includes a SfnStatusEnum status.
 */
export const useQuerySfnReadSingleExecutionStatus = (executionArn: string) => {
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);

	return useQuery<SfnSchema | undefined, ApiError>(
		[QUERY_KEYS.pipeline.SingleSfnExecutionStatus, executionArn, user],
		async () => {
			try {
				if (client === undefined) {
					throw new Error('API client is not initialized');
				}
				if (user.attributes === undefined) {
					throw new Error('AWS Amplify user attributes cannot be undefined');
				}
				if (executionArn === undefined || executionArn === '') {
					throw new Error('No executionArn provided');
				}
				const response = await client.pipeline.sfnReadSingleExecutionStatusApiV2ReadSingleSfnExecutionGet(
					executionArn
				);
				logRequest({ executionArn }, 'GET', QUERY_KEYS.pipeline.SingleSfnExecutionStatus, response);
				return response;
			} catch (error: unknown) {
				if (error instanceof ApiError) {
					logApiError(error);
				} else if (error instanceof AxiosError) {
					logAxiosError(error);
				} else {
					logError(error, 'Error fetching single step-function execution status');
				}

				throw error;
			}
		},
		{
			enabled: !!user.attributes?.email
		}
	);
};


/**
 * Custom hook to fetch invoice errors using React Query's useQuery.
 * @returns QueryResult containing data, error, and status of the query.
 */
export const useGetInvoiceErrors = () => {
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);
  
	return useQuery<Array<InvoicesErrorHistory>, ApiError>(
	  QUERY_KEYS.pipeline.InvoiceErrors,
	  async () => {
		if (client === undefined) {
		  throw new Error('API client is not initialized');
		}
		if (user.attributes === undefined) {
			throw new Error('AWS Amplify user attributes cannot be undefined');
		}
		const response = await client.pipeline.getInvoiceErrorsApiV2PipelineErrorsGet();
		return response;
	  },
	  {
		onSuccess: (data) => {
			// Perform any side effects here
			console.log('Invoice errors fetched successfully:', data);
		  },
		onError: (error) => {
		  // Error handling
		  if (error instanceof ApiError) {
			logApiError(error);
		  } else {
			logError(error, 'Error fetching invoice errors');
		  }
		},
	  }
	);
  };

  export const useMutationRetryInvoiceUpload = () => {
    const { client } = useApiNoSqlContext();
    const { user } = useAuthenticator(context => [context.user]);
    const queryClient = useQueryClient();

    return useMutation<EmailBucketResponse, ApiError, Body_do_retry_api_v2_pipeline_retry_post>(
        (body: Body_do_retry_api_v2_pipeline_retry_post) => {
            if (!client) {
                throw new Error('API client is not initialized');
            }
            if (user.attributes === undefined) {
                throw new Error('AWS Amplify user attributes cannot be undefined');
            }
            if (!body.pdf_uuid) {
                throw new Error('No pdf_uuid provided');
            }
            const response = client.pipeline.doRetryApiV2PipelineRetryPost(body);
            logRequest(body, 'POST', MUTATION_KEYS.pipeline.InvoiceRetry, response);
            return response;
        },
        {
            onSuccess: (data: EmailBucketResponse) => {
                console.log('Retry successful:', JSON.stringify(data));
                queryClient.invalidateQueries(QUERY_KEYS.pipeline.InvoiceRetry);
            },
            onError: (error: unknown) => {
                if (error instanceof ApiError) {
                    logApiError(error);
                } else if (error instanceof AxiosError) {
                    logAxiosError(error);
                } else {
                    logError(error, 'Error retrying invoice upload');
                }
                return;
            }
        }
    );
};

export const useMutationRetryAllInvoiceUpload = () => {
    const { client } = useApiNoSqlContext();
    const { user } = useAuthenticator(context => [context.user]);
    const queryClient = useQueryClient();

    return useMutation<EmailBucketResponse[], ApiError, string[]>(
        async (pdfUuids: string[]) => {
            if (!client) {
                throw new Error('API client is not initialized');
            }
            if (!user.attributes) {
                throw new Error('AWS Amplify user attributes cannot be undefined');
            }
            if (!pdfUuids || pdfUuids.length === 0) {
                throw new Error('No pdf_uuid(s) provided for retry');
            }

            // Send the array of strings directly as the request body
            const response = await client.pipeline.doRetryMultipleFilesApiV2PipelineRetryMultiplePost(pdfUuids);

            logRequest(pdfUuids, 'POST', MUTATION_KEYS.pipeline.InvoiceRetryAll, response);
            return response;
        },
        {
            onSuccess: (data: EmailBucketResponse[]) => {
                console.log('Retry unsuccessful for multiple files:', JSON.stringify(data));

                // Invalidate necessary queries to refresh data
                queryClient.invalidateQueries(QUERY_KEYS.pipeline.InvoiceRetryAll);
                queryClient.invalidateQueries(QUERY_KEYS.pipeline.InvoiceErrors);
                queryClient.invalidateQueries(QUERY_KEYS.invoices.AllInvoices);
            },
            onError: (error: unknown) => {
                if (error instanceof ApiError) {
                    logApiError(error);
                } else if (error instanceof AxiosError) {
                    logAxiosError(error);
                } else {
                    logError(error, 'Error retrying multiple invoice uploads');
                }
            }
        }
    );
};


export const useMutationResolveInvoiceUploadError = () => {
    const { client } = useApiNoSqlContext();
    const { user } = useAuthenticator(context => [context.user]);
    const queryClient = useQueryClient();

    return useMutation<string, ApiError, Body_delete_invoice_upload_pipeline_error_api_v2_pipeline_resolve_post>(
        (body: Body_delete_invoice_upload_pipeline_error_api_v2_pipeline_resolve_post) => {
            if (!client) {
                throw new Error('API client is not initialized');
            }
            if (user.attributes === undefined) {
                throw new Error('AWS Amplify user attributes cannot be undefined');
            }
            if (!body.pdf_uuid) {
                throw new Error('No pdf_uuid provided');
            }
            const response = client.pipeline.deleteInvoiceUploadPipelineErrorApiV2PipelineResolvePost(body);
            logRequest(body, 'POST', MUTATION_KEYS.pipeline.InvoiceUploadDelete, response);
            return response;
        },
        {
            onSuccess: (data: string) => {
                console.log('Resolve successful:', JSON.stringify(data));
                queryClient.invalidateQueries(QUERY_KEYS.pipeline.InvoiceUploadDelete);
            },
            onError: (error: unknown) => {
                if (error instanceof ApiError) {
                    logApiError(error);
                } else if (error instanceof AxiosError) {
                    logAxiosError(error);
                } else {
                    logError(error, 'Error resolving invoice upload');
                }
            }
        }
    );
};

export const useMutationUploadMultiplePdfsToEmailBucket = () => {
    const { client } = useApiNoSqlContext();
    const { user } = useAuthenticator(context => [context.user]);
    const queryClient = useQueryClient();

    return useMutation<Array<EmailBucketResponse>, ApiError, Body_upload_pdf_to_email_bucket_api_v2_pipeline_upload_pdf_post>(
        (formData: Body_upload_pdf_to_email_bucket_api_v2_pipeline_upload_pdf_post) => {
            if (!client) {
                throw new Error('API client is not initialized');
            }
            if (user.attributes === undefined) {
                throw new Error('AWS Amplify user attributes cannot be undefined');
            }
            if (!formData || !formData.files || formData.files.length === 0) {
                throw new Error('No files provided in form data');
            }

            // Call the API client method for uploading multiple PDFs
            const response = client.pipeline.uploadPdfToEmailBucketApiV2PipelineUploadPdfPost(formData);
            logRequest(formData, 'POST', MUTATION_KEYS.pipeline.InvoiceUploadEmailBucket, response);
            return response;
        },
        {
            onSuccess: (data: Array<EmailBucketResponse>) => {
                console.log('PDF upload successful for multiple files:', JSON.stringify(data));
                
                // Invalidate related queries to refresh the data
                queryClient.invalidateQueries(QUERY_KEYS.pipeline.InvoiceUploadEmailBucket);
                queryClient.invalidateQueries(QUERY_KEYS.invoices.AllInvoices);
                queryClient.invalidateQueries(QUERY_KEYS.utilities.AllUtilities);
                queryClient.invalidateQueries(QUERY_KEYS.prompts.AllPrompts);
                queryClient.invalidateQueries(QUERY_KEYS.common.DocumentCount);
                queryClient.invalidateQueries(QUERY_KEYS.ecsHealthCheck.SingleHealthCheck);
            },
            onError: (error: unknown) => {
                if (error instanceof ApiError) {
                    logApiError(error);
                } else if (error instanceof AxiosError) {
                    logAxiosError(error);
                } else {
                    logError(error, 'Error uploading PDFs to email bucket');
                }
            }
        }
    );
};

